import React, { useState, useEffect, useRef } from "react";
import { useI18n } from "../lib/i18n";
import { http, formatApiErrorDetail } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import GoogleAddressInput from "./GoogleAddressInput";

const HAS_GOOGLE_KEY = !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function OSMAddressInput({ value, onChange, placeholder, testid }) {
  const [q, setQ] = useState(value?.label || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const timer = useRef();
  const lastQuery = useRef("");

  useEffect(() => { setQ(value?.label || ""); }, [value?.label]);

  // When the user types, propagate the typed string as a free-form value so the booking
  // can proceed even without picking a suggestion (server geocodes during quote).
  const propagateTyped = (text) => {
    setQ(text);
    setErrMsg(null);
    if (!text) onChange(null);
    else if (text !== value?.label) onChange({ label: text, lat: null, lon: null });
  };

  useEffect(() => {
    if (!q || q.length < 3) { setResults([]); setOpen(false); return; }
    if (q === lastQuery.current && results.length > 0) { return; }
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await http.get("/geocode/search", { params: { q } });
        lastQuery.current = q;
        const arr = Array.isArray(data) ? data : [];
        setResults(arr);
        setOpen(arr.length > 0);
        if (arr.length === 0) setErrMsg("No matches — you can still type the full address and continue.");
      } catch (e) {
        // Surface error in console so we can diagnose CDN/CORS/Cloudflare issues
        console.warn("[address-autocomplete] /geocode/search failed:", e?.response?.status, e?.message);
        setErrMsg("Address service temporarily unavailable — type the full address and continue.");
        setResults([]);
        setOpen(false);
      } finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q]); // eslint-disable-line

  return (
    <div className="relative">
      <Input
        data-testid={testid}
        value={q}
        onChange={(e) => propagateTyped(e.target.value)}
        placeholder={placeholder}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className="bg-[#040814] border-white/20 text-white focus:border-[#D4AF37] h-12"
        autoComplete="off"
      />
      {loading && <Loader2 className="w-4 h-4 absolute right-3 top-4 animate-spin text-[#D4AF37]" />}
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-[#0A1128] border border-white/15 rounded-md shadow-2xl max-h-72 overflow-y-auto">
          {results.map((r, i) => (
            <button
              type="button"
              key={i}
              data-testid={`${testid}-suggestion-${i}`}
              onClick={() => { onChange(r); setQ(r.label); setOpen(false); setErrMsg(null); }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 text-white/90 border-b border-white/5 last:border-0"
            >
              {r.label}
            </button>
          ))}
        </div>
      )}
      {errMsg && !loading && results.length === 0 && (
        <p className="text-[11px] text-white/50 mt-1" data-testid={`${testid}-info`}>{errMsg}</p>
      )}
    </div>
  );
}

// Switch to Google Places when key is configured, otherwise use free OSM fallback.
const AddressInput = HAS_GOOGLE_KEY ? GoogleAddressInput : OSMAddressInput;

export default function BookingForm({ defaultAirport = null, onBooked }) {
  const { t } = useI18n();
  const [pickup, setPickup] = useState(null);
  const [destination, setDestination] = useState(null);
  const [airports, setAirports] = useState([]);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    time: "12:00",
    passengers: 1, luggage: 1,
    trip_type: "oneway",
    return_date: "", return_time: "",
    customer_name: "", customer_email: "", customer_phone: "", notes: "",
    airport_code: defaultAirport || "",
    arrival_type: "international",
    waiting_min: 0,
    return_standby_min: 0,
    promo_code: "",
  });
  const [waitingTouched, setWaitingTouched] = useState(false);
  const [promoMsg, setPromoMsg] = useState(null);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    http.get("/airports").then(({ data }) => setAirports(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (defaultAirport) setForm((f) => ({ ...f, airport_code: defaultAirport }));
  }, [defaultAirport]);

  // Auto-suggest waiting minutes when airport + arrival_type change
  useEffect(() => {
    if (waitingTouched) return;
    if (!form.airport_code) { setForm((f) => ({ ...f, waiting_min: 0 })); return; }
    const a = airports.find((x) => x.code === form.airport_code);
    if (!a) return;
    const suggested = form.arrival_type === "domestic"
      ? (a.free_wait_dom ?? 10)
      : (a.free_wait_intl ?? 15);
    setForm((f) => ({ ...f, waiting_min: suggested }));
  }, [form.airport_code, form.arrival_type, airports, waitingTouched]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildPayload = () => ({
    pickup: pickup || { label: "" },
    destination: destination || { label: "" },
    date: form.date, time: form.time,
    passengers: Number(form.passengers), luggage: Number(form.luggage),
    trip_type: form.trip_type,
    return_date: form.return_date || null, return_time: form.return_time || null,
    customer_name: form.customer_name, customer_email: form.customer_email || null, customer_phone: form.customer_phone,
    notes: form.notes,
    airport_code: form.airport_code || null,
    arrival_type: form.airport_code ? form.arrival_type : null,
    waiting_min: Number(form.waiting_min) || 0,
    return_standby_min: form.trip_type === "return" ? (Number(form.return_standby_min) || 0) : 0,
    promo_code: form.promo_code?.trim() || null,
  });

  const applyPromo = async () => {
    setPromoMsg(null);
    if (!form.promo_code?.trim()) return;
    try {
      const { data } = await http.get("/promo/validate", { params: { code: form.promo_code.trim() } });
      const pct = data.discount_pct ? `${data.discount_pct}% off` : `€${data.discount_amount} off`;
      setPromoMsg({ ok: true, text: `${data.code} applied — ${pct}` });
      await getQuote();
    } catch (e) {
      setPromoMsg({ ok: false, text: formatApiErrorDetail(e.response?.data?.detail) || "Invalid code" });
    }
  };

  const getQuote = async () => {
    if (!pickup?.label || !destination?.label) {
      toast.error("Please select pickup and destination");
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.post("/quote", buildPayload());
      setQuote(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const bookAndPay = async () => {
    if (!quote) { await getQuote(); return; }
    if (!form.customer_email || !form.customer_name) {
      toast.error("Please enter your name and email");
      return;
    }
    setPaying(true);
    try {
      const { data: booking } = await http.post("/bookings", buildPayload());
      const { data: pay } = await http.post("/payments/create", { reference: booking.reference });
      onBooked?.(booking);
      if (pay.disabled) {
        toast.success(`Booking ${booking.reference} created. Payment in demo mode.`);
        window.location.href = pay.checkout_url;
      } else {
        window.location.href = pay.checkout_url;
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setPaying(false); }
  };

  return (
    <div className="glass rounded-2xl p-6 lg:p-8 shadow-2xl w-full max-w-2xl">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-1">{t("booking.subtitle")}</div>
        <h3 className="font-heading text-2xl text-white">{t("booking.title")}</h3>
      </div>

      <Tabs value={form.trip_type} onValueChange={(v) => update("trip_type", v)} className="mb-5">
        <TabsList className="bg-[#040814] border border-white/10">
          <TabsTrigger value="oneway" data-testid="trip-oneway" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">{t("booking.oneway")}</TabsTrigger>
          <TabsTrigger value="return" data-testid="trip-return" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#040814]">{t("booking.return")}</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60">{t("booking.pickup")}</Label>
          <AddressInput value={pickup} onChange={setPickup} placeholder="e.g. Dam Square, Amsterdam" testid="pickup-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60">{t("booking.destination")}</Label>
          <AddressInput value={destination} onChange={setDestination} placeholder="e.g. Schiphol Airport" testid="destination-input" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">{t("booking.date")}</Label>
            <Input data-testid="booking-date" type="date" value={form.date} onChange={(e) => update("date", e.target.value)} className="bg-[#040814] border-white/20 text-white h-12" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">{t("booking.time")}</Label>
            <Input data-testid="booking-time" type="time" value={form.time} onChange={(e) => update("time", e.target.value)} className="bg-[#040814] border-white/20 text-white h-12" />
          </div>
        </div>

        {form.trip_type === "return" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60">{t("booking.return_date")}</Label>
              <Input data-testid="booking-return-date" type="date" value={form.return_date} onChange={(e) => update("return_date", e.target.value)} className="bg-[#040814] border-white/20 text-white h-12" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60">{t("booking.return_time")}</Label>
              <Input data-testid="booking-return-time" type="time" value={form.return_time} onChange={(e) => update("return_time", e.target.value)} className="bg-[#040814] border-white/20 text-white h-12" />
            </div>
          </div>
        )}

        {form.trip_type === "return" && (
          <div className="rounded-xl border border-white/10 bg-[#040814]/60 p-4">
            <Label className="text-xs uppercase tracking-widest text-[#D4AF37]">Standby (return-trip waiting)</Label>
            <p className="text-xs text-white/50 mt-1 mb-3">Driver waits at destination before the return ride. Charged at standby rate per minute.</p>
            <div className="flex flex-wrap gap-2">
              {[
                { label: "None", min: 0 },
                { label: "1h", min: 60 },
                { label: "2h", min: 120 },
                { label: "3h", min: 180 },
                { label: "4h", min: 240 },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.min}
                  data-testid={`standby-${opt.min}`}
                  onClick={() => update("return_standby_min", opt.min)}
                  className={`px-4 h-10 rounded-md border text-sm transition-colors ${Number(form.return_standby_min) === opt.min ? "bg-[#D4AF37] text-[#040814] border-[#D4AF37] font-semibold" : "bg-[#040814] border-white/20 text-white/70 hover:border-white/40"}`}
                >
                  {opt.label}
                </button>
              ))}
              <Input
                data-testid="standby-custom"
                type="number" min={0} max={720} step={15}
                value={form.return_standby_min}
                onChange={(e) => update("return_standby_min", e.target.value)}
                placeholder="custom min"
                className="bg-[#040814] border-white/20 text-white h-10 w-32"
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">{t("booking.passengers")}</Label>
            <Input data-testid="booking-passengers" type="number" min={1} max={8} value={form.passengers} onChange={(e) => update("passengers", e.target.value)} className="bg-[#040814] border-white/20 text-white h-12" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">{t("booking.luggage")}</Label>
            <Input data-testid="booking-luggage" type="number" min={0} max={10} value={form.luggage} onChange={(e) => update("luggage", e.target.value)} className="bg-[#040814] border-white/20 text-white h-12" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">{t("booking.airport_select")}</Label>
            <Select value={form.airport_code || "none"} onValueChange={(v) => update("airport_code", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="booking-airport" className="bg-[#040814] border-white/20 text-white h-12">
                <SelectValue placeholder={t("booking.none")} />
              </SelectTrigger>
              <SelectContent className="bg-[#0A1128] text-white border-white/10">
                <SelectItem value="none">{t("booking.none")}</SelectItem>
                {airports.map((a) => (
                  <SelectItem key={a.code} value={a.code}>{a.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="customer-name" placeholder={t("booking.name")} value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} className="bg-[#040814] border-white/20 text-white h-12" />
          <Input data-testid="customer-phone" placeholder={t("booking.phone")} value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} className="bg-[#040814] border-white/20 text-white h-12" />
        </div>
        <Input data-testid="customer-email" type="email" placeholder={t("booking.email")} value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} className="bg-[#040814] border-white/20 text-white h-12" />

        {form.airport_code && (
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">Arrival type</Label>
            <div className="flex gap-2 mt-1">
              <button type="button" data-testid="arrival-intl"
                onClick={() => { update("arrival_type", "international"); setWaitingTouched(false); }}
                className={`flex-1 h-11 rounded-md border text-sm transition-colors ${form.arrival_type === "international" ? "bg-[#D4AF37] text-[#040814] border-[#D4AF37] font-semibold" : "bg-[#040814] border-white/20 text-white/70 hover:border-white/40"}`}>
                International
              </button>
              <button type="button" data-testid="arrival-dom"
                onClick={() => { update("arrival_type", "domestic"); setWaitingTouched(false); }}
                className={`flex-1 h-11 rounded-md border text-sm transition-colors ${form.arrival_type === "domestic" ? "bg-[#D4AF37] text-[#040814] border-[#D4AF37] font-semibold" : "bg-[#040814] border-white/20 text-white/70 hover:border-white/40"}`}>
                Domestic
              </button>
            </div>
          </div>
        )}

        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60">Waiting time (minutes) — optional</Label>
          <Input data-testid="booking-waiting" type="number" min={0} max={120} value={form.waiting_min}
            onChange={(e) => { setWaitingTouched(true); update("waiting_min", e.target.value); }}
            placeholder="0" className="bg-[#040814] border-white/20 text-white h-12" />
          <p className="text-xs text-white/40 mt-1">
            {form.airport_code
              ? `${form.arrival_type === "domestic" ? "Domestic" : "International"} arrival at ${form.airport_code}: suggested free waiting auto-filled. Extra minutes charged at €1/min.`
              : "First 5 minutes are free. Additional waiting time is charged at €1/min."}
          </p>
        </div>

        {quote && (
          <div className="rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Fare breakdown</div>
            <div data-testid="fare-items" className="space-y-1.5 text-sm">
              {(quote.items || []).map((it, i) => (
                <div key={i} data-testid={`fare-item-${it.key}`} className="flex justify-between gap-3 items-baseline">
                  <div className="text-white/85">
                    {it.label}
                    {it.detail && <span className="text-white/40 text-xs ml-2">{it.detail}</span>}
                  </div>
                  <div className={`tabular-nums ${it.amount < 0 ? "text-emerald-300" : "text-white"}`}>
                    {it.amount < 0 ? "−" : ""}€{Math.abs(it.amount).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[#D4AF37]/30 flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Total Amount</span>
              <span data-testid="quote-total" className="font-heading text-4xl text-gold-gradient">€{quote.total.toFixed(2)}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-white/40">
              <span>{t("booking.distance")}: {quote.distance_km} km</span>
              <span>{t("booking.duration")}: {Math.round(quote.duration_min)} min</span>
            </div>

            {quote.below_min && (
              <div data-testid="below-min-warning" className="mt-4 rounded-lg border border-amber-400/50 bg-amber-500/10 p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-amber-300 mb-2">
                  Minimum booking amount: €{Number(quote.min_booking_amount || 55).toFixed(0)}
                </div>
                <p className="text-sm text-white/90 leading-relaxed">
                  Minimum booking amount is €{Number(quote.min_booking_amount || 55).toFixed(0)}. Please contact us by phone or WhatsApp for short rides.
                </p>
              </div>
            )}
          </div>
        )}

        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60">Promo code (optional)</Label>
          <div className="flex gap-2">
            <Input
              data-testid="promo-input"
              value={form.promo_code}
              onChange={(e) => { update("promo_code", e.target.value.toUpperCase()); setPromoMsg(null); }}
              placeholder="e.g. WELCOME10"
              className="bg-[#040814] border-white/20 text-white h-12 uppercase"
            />
            <Button
              type="button"
              data-testid="promo-apply"
              onClick={applyPromo}
              className="h-12 px-5 bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              Apply
            </Button>
          </div>
          {promoMsg && (
            <p data-testid="promo-msg" className={`text-xs mt-1 ${promoMsg.ok ? "text-emerald-400" : "text-red-400"}`}>{promoMsg.text}</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            data-testid="get-quote-btn"
            onClick={getQuote}
            disabled={busy}
            className="flex-1 h-12 bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {t("booking.quote")}
          </Button>
          <Button
            data-testid="book-pay-btn"
            onClick={bookAndPay}
            disabled={paying || !quote || quote?.below_min}
            className="flex-1 h-12 bg-[#D4AF37] hover:bg-[#B5952F] text-[#040814] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {t("booking.pay")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
